import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { getUploadUrl } from '../utils/uploadUrl';

function NewsPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentForm, setCommentForm] = useState({ name: '', email: '', comment: '' });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [otherNews, setOtherNews] = useState([]);
  const [loadingOtherNews, setLoadingOtherNews] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`/api/news/${id}`);
        const postData = response.data;
        // If post has images array, use it; otherwise create array from single image
        if (postData.images && postData.images.length > 0) {
          // Ensure images are in the correct format
          const formattedImages = postData.images.map(img => {
            if (typeof img === 'string') {
              return { filename: img, type: 'image' };
            }
            return img;
          });
          setPost({ ...postData, images: formattedImages });
        } else if (postData.image) {
          setPost({ ...postData, images: [{ filename: postData.image, type: 'image' }] });
        } else {
          setPost(postData);
        }
        setLikeCount(parseInt(postData.like_count) || 0);
      } catch (error) {
        console.error('Error fetching news post:', error);
      } finally {
        setLoading(false);
      }
    };

    const checkLikeStatus = async () => {
      try {
        const response = await axios.get(`/api/news/${id}/like-status`);
        setLiked(response.data.liked);
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };

    fetchPost();
    checkLikeStatus();
    fetchComments();
    fetchOtherNews();
  }, [id]);

  const fetchOtherNews = async () => {
    setLoadingOtherNews(true);
    try {
      const response = await axios.get('/api/news');
      // Filter out the current post and get up to 5 other posts
      const filtered = response.data.filter(p => p.id !== id).slice(0, 5);
      setOtherNews(filtered);
    } catch (error) {
      console.error('Error fetching other news:', error);
    } finally {
      setLoadingOtherNews(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`/api/news/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async () => {
    try {
      const response = await axios.post(`/api/news/${id}/like`);
      setLiked(response.data.liked);
      setLikeCount(parseInt(response.data.likeCount) || 0);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentForm.name.trim() || !commentForm.comment.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmittingComment(true);
    try {
      const response = await axios.post(`/api/news/${id}/comments`, commentForm);
      if (response.status === 202 || response.data?.status === 'pending') {
        alert('Comment submitted and pending moderation.');
      } else {
        setComments([response.data, ...comments]);
        setShowComments(true); // Auto-expand comments after submitting
      }
      setCommentForm({ name: '', email: '', comment: '' });
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <h2>Post not found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row tgh-page-with-sidebar" style={{ alignItems: 'flex-start' }}>
        <div className="col-xl-8 col-lg-7 col-12">
          <article className="tgh-news-article">
            <h1 className="display-4 mb-3" style={{ color: '#1a1a1a', fontWeight: '600' }}>{post.title}</h1>
            <div className="mb-4 text-muted" style={{ fontSize: '0.95rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '1rem' }}>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <span>
                  <strong>Date:</strong> {formatDate(post.date)}
                </span>
                <span>•</span>
                <span>
                  <strong>Author:</strong> {post.username || post.full_name || 'Admin'}
                </span>
              </div>
            </div>
            {/* Image Carousel */}
            {(post.images && post.images.length > 0) || post.image ? (
              <div className="mb-4 news-post-carousel">
                {post.images && post.images.length > 1 ? (
                  <Swiper
                    className="news-post-swiper"
                    modules={[Autoplay, Pagination, Navigation]}
                    slidesPerView={1}
                    loop
                    speed={500}
                    autoplay={{
                      delay: 4000,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    pagination={{ clickable: true }}
                    navigation
                    grabCursor>
                    {post.images.map((item, index) => {
                      const filename = typeof item === 'string' ? item : item.filename;
                      const mediaType = typeof item === 'string' ? 'image' : (item.type || 'image');
                      return (
                        <SwiperSlide key={`${filename}-${index}`}>
                          <div className="news-carousel-image-container">
                            {mediaType === 'video' ? (
                              <video
                                src={getUploadUrl(filename)}
                                className="news-carousel-image"
                                controls
                              />
                            ) : (
                              <img
                                src={getUploadUrl(filename)}
                                alt={`${post.title} - Image ${index + 1}`}
                                className="news-carousel-image"
                                loading="eager"
                                decoding="async"
                                onError={(e) => {
                                  e.target.src = '/img/Gallery/opd1.jpg';
                                }}
                              />
                            )}
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                ) : (
                  <div className="news-carousel-image-container single-image">
                    {(() => {
                      const firstItem = post.images && post.images.length > 0 ? post.images[0] : (post.image ? { filename: post.image, type: 'image' } : null);
                      if (!firstItem) return null;
                      const filename = typeof firstItem === 'string' ? firstItem : firstItem.filename;
                      const mediaType = typeof firstItem === 'string' ? 'image' : (firstItem.type || 'image');
                      return mediaType === 'video' ? (
                        <video
                          src={getUploadUrl(filename)}
                          className="news-carousel-image"
                          controls
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <img
                          src={getUploadUrl(filename)}
                          className="news-carousel-image"
                          alt={post.title}
                          onError={(e) => {
                            e.target.src = '/img/Gallery/opd1.jpg';
                          }}
                        />
                      );
                    })()}
                  </div>
                )}
              </div>
            ) : null}
            <div
              className="content mb-4 tgh-news-post-body"
              style={{ fontSize: '1.05rem', lineHeight: '1.8', color: '#333', textAlign: 'justify' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
            />
            
            {/* Like Button */}
            <div className="tgh-news-actions mb-4" style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '1.5rem 0' }}>
              <button
                onClick={handleLike}
                className={`btn ${liked ? 'btn-danger' : 'btn-outline-danger'}`}
                style={{
                  borderRadius: '4px',
                  padding: '0.5rem 1.5rem',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  backgroundColor: liked ? '#dc3545' : 'transparent',
                  borderColor: '#dc3545',
                  color: liked ? '#fff' : '#dc3545'
                }}
              >
                <i className={`bi ${liked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
              </button>
            </div>

            {/* Comments Section - Dropdown */}
            <div className="tgh-news-comments mt-5">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: 0, color: '#1a1a1a' }}>
                  Comments ({comments.length})
                </h3>
                <button
                  onClick={() => setShowComments(!showComments)}
                  className="btn btn-outline-secondary btn-sm"
                  style={{ borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <i className={`bi ${showComments ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {showComments ? 'Hide' : 'View'} Comments
                </button>
              </div>

              {/* Comment Form */}
              <div className="card mb-4" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                <div className="card-body p-4">
                  <h5 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem' }}>Leave a Comment</h5>
                  <form onSubmit={handleCommentSubmit}>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontWeight: '500' }}>Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={commentForm.name}
                          onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                          required
                          style={{ borderRadius: '4px' }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" style={{ fontWeight: '500' }}>Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={commentForm.email}
                          onChange={(e) => setCommentForm({ ...commentForm, email: e.target.value })}
                          style={{ borderRadius: '4px' }}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label" style={{ fontWeight: '500' }}>Comment <span className="text-danger">*</span></label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={commentForm.comment}
                        onChange={(e) => setCommentForm({ ...commentForm, comment: e.target.value })}
                        required
                        style={{ borderRadius: '4px' }}
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submittingComment}
                      style={{ borderRadius: '4px', padding: '0.5rem 1.5rem', fontWeight: '500' }}
                    >
                      {submittingComment ? 'Submitting...' : 'Submit Comment'}
                    </button>
                  </form>
                </div>
              </div>

              {/* Comments List - Collapsible */}
              {showComments && (
                <div className="comments-dropdown">
                  {loadingComments ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading comments...</span>
                      </div>
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                      <div className="card-body p-4 text-center">
                        <p className="text-muted mb-0">No comments yet. Be the first to comment!</p>
                      </div>
                    </div>
                  ) : (
                    <div className="comments-list">
                      {comments.map((comment) => (
                        <div key={comment.id} className="card mb-3" style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 style={{ fontWeight: '600', marginBottom: '0.25rem', color: '#1a1a1a' }}>{comment.name}</h6>
                              </div>
                              <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                {new Date(comment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </small>
                            </div>
                            <p style={{ marginBottom: 0, color: '#333', lineHeight: '1.6' }}>{comment.comment}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </article>
        </div>
        
        {/* More News Sidebar */}
        <div className="col-xl-4 col-lg-5 col-12 tgh-sidebar-col mt-4 mt-xl-0">
          <div className="more-news-sidebar">
            <div className="card" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid var(--tgh-blue-light)', borderRadius: '8px 8px 0 0' }}>
                <h4 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a' }}>
                  More News
                </h4>
              </div>
              <div className="card-body p-3" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {loadingOtherNews ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : otherNews.length === 0 ? (
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>No other news available</p>
                ) : (
                  <div className="list-group list-group-flush">
                    {otherNews.map((newsItem) => (
                      <Link
                        key={newsItem.id}
                        to={`/news/${newsItem.id}`}
                        className="list-group-item list-group-item-action"
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #e0e0e0',
                          padding: '1rem 0',
                          textDecoration: 'none',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          {newsItem.image && (
                            <div style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                src={getUploadUrl(newsItem.image)}
                                alt={newsItem.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  objectPosition: 'center'
                                }}
                                onError={(e) => {
                                  e.target.src = '/img/Gallery/opd1.jpg';
                                }}
                              />
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <h6 className="mb-1" style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.3' }}>
                              {newsItem.title}
                            </h6>
                            <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                              {formatDate(newsItem.date)}
                            </small>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                <div className="mt-3 text-center">
                  <Link
                    to="/news"
                    className="btn btn-outline-primary btn-sm"
                    style={{ borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    View All News <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewsPost;
